import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";

export default function DoctorSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDoctor, setIsDoctor] = useState(false);
  const [therapyType, setTherapyType] = useState("CBT");
  const [therapyOptions, setTherapyOptions] = useState([]);

  useEffect(() => {
    // Check if user is a doctor
    const storedUser = localStorage.getItem("auth");
    if (storedUser) {
      try {
        const authData = JSON.parse(storedUser);
        if (authData.token) {
          const decoded = jwtDecode(authData.token);
          const role = decoded.sub?.role?.toLowerCase() || authData.role?.toLowerCase();
          const isDoctorRole = role === "doctor";
          setIsDoctor(isDoctorRole);
          
          if (!isDoctorRole) {
            toast.error("Only doctors can access this page");
            navigate("/dashboard");
            return;
          }
        }
      } catch (err) {
        console.error("Error decoding token:", err);
        navigate("/dashboard");
        return;
      }
    }

    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.getDoctorSettings();
      setTherapyType(res.data.therapy_type || "CBT");
      setTherapyOptions(res.data.therapy_options || []);
    } catch (err) {
      console.error("Error fetching doctor settings:", err);
      if (err.response?.status === 403) {
        toast.error("Only doctors can access this page");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load settings. Using default values.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateDoctorSettings({ therapy_type: therapyType });
      toast.success("Settings saved successfully!");
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error(err.response?.data?.error || "Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full px-4">
          <p className="text-sm md:text-base text-gray-600">Loading settings...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!isDoctor) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full px-4">
          <p className="text-sm md:text-base text-red-600">Access denied. Only doctors can view this page.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-full md:max-w-4xl lg:max-w-6xl mx-auto py-4 md:py-6 px-4 md:px-6">
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 lg:p-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">Doctor Settings</h1>
          </div>

          {/* Therapy Type Selection */}
          <div className="mb-6">
            <label className="block text-base md:text-lg font-medium text-gray-700 mb-3">
              Preferred Therapy Approach
            </label>
            <p className="text-sm md:text-base text-gray-500 mb-4">
              Select your primary therapy approach. This will be used to evaluate your session performance
              and provide framework-specific feedback in session summaries.
            </p>
            
            <div className="space-y-4">
              {therapyOptions.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    therapyType === option.value
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="therapyType"
                    value={option.value}
                    checked={therapyType === option.value}
                    onChange={(e) => setTherapyType(e.target.value)}
                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 flex-shrink-0"
                  />
                  <div className="ml-4">
                    <span className="block text-base md:text-lg font-semibold text-gray-800">
                      {option.label}
                    </span>
                    <span className="block text-sm md:text-base text-gray-500 mt-1">
                      {option.description}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-base md:text-lg font-medium text-blue-800 mb-2">
              How This Affects Your Sessions
            </h3>
            <ul className="text-sm md:text-base text-blue-700 space-y-1">
              <li>• Session summaries will evaluate your performance based on the selected therapy framework</li>
              <li>• Feedback will be tailored to the techniques and principles of your chosen approach</li>
              <li>• You can change this setting at any time before starting a new session</li>
            </ul>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto px-8 py-3 text-base md:text-lg font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
